import React from "react";
import { NavLink } from "react-router-dom";
import Chatbot from "../Chatbot";
import { trackCTA } from "../../utils/analytics";

const AboutUs = () => {
  return (
    <div className="about-us">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-jnanakshi-500 via-jnanakshi-400 to-jnanakshi-600 text-white pt-16 sm:pt-20 overflow-hidden">
        {/* Subtle background image overlay */}
        <div
          className="absolute inset-0 bg-center bg-cover opacity-15 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url('/images/about-hero-overlay.jpg')"
          }}
        />
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
              About Us
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-gray-200 max-w-4xl mx-auto">
            Jnanakshi Solutions is dedicated to bridging the gap between academic knowledge and professional competence through immersive Live Workshops, Trainings, and Development services. We deliver programs both Online and Offline, and provide individual mentoring for students seeking personalized guidance.
            </p>
          </div>
        </div>

        {/*WAVES*/}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] rotate-180">
          <svg
            className="relative block w-[calc(100%+1.3px)] h-[111px]"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              className="fill-white opacity-25"
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              className="fill-white opacity-50"
            ></path>
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              className="fill-gray-50"
            ></path>
          </svg>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="bg-gray-50 dark:bg-dark-800 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            {/* Mission */}
            <div className="bg-white dark:bg-dark-700 p-6 sm:p-8 rounded-lg shadow-lg transition-colors duration-300 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              <img src="/images/about-mission.jpg" alt="Mission" className="w-full h-36 object-cover rounded-md mb-3 sm:mb-4" loading="lazy" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">Our Mission</h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                Empower learners — from school students to graduates — to become confident and industry‑ready by providing immersive live workshops, focused training programs, and structured career guidance. 
                We help school students (8th–12th) and college students understand the full spectrum of career paths available to them, so they can make informed decisions early.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white dark:bg-dark-700 p-6 sm:p-8 rounded-lg shadow-lg transition-colors duration-300 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              <img src="/images/about-vision.jpg" alt="Vision" className="w-full h-36 object-cover rounded-md mb-3 sm:mb-4" loading="lazy" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">Our Vision</h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                A world where students from school to postgraduate levels seamlessly transition from education to meaningful careers with confidence, competence, and expert guidance — 
                supported by mentors with 25+ years of industry experience, diversified domain knowledge, and industry‑recognized subject matter expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      

      {/* Values Section */}
      <section className="bg-white dark:bg-dark-900 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-white text-center mb-8 sm:mb-10 lg:mb-12">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
              title: "Excellence",
              description: "Delivering the highest quality training and mentorship programs.",
              image: "/images/about-value-excellence.jpg"
              },
              {
                title: "Connection",
                description: "Building bridges between students and industry professionals.",
              image: "/images/about-value-connection.jpg"
              },
              {
                title: "Transformation",
                description: "Transforming academic knowledge into professional competence.",
              image: "/images/about-value-transformation.jpg"
              },
              {
                title: "Empowerment",
                description: "Empowering students to achieve their career goals.",
              image: "/images/about-value-empowerment.jpg"
              }
            ].map((value, index) => (
              <div key={index} className="text-center p-4 sm:p-6 bg-gray-50 dark:bg-dark-800 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                <img src={value.image} alt={value.title} className="w-full h-28 object-cover rounded mb-3 sm:mb-4" loading="lazy" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white mb-2">{value.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="bg-gradient-to-r from-jnanakshi-500 to-jnanakshi-600 text-white py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            Ready to Transform Your Career?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-gray-200 max-w-3xl mx-auto">
            Let's discuss how Jnanakshi Solutions can help you bridge the gap between academic knowledge and professional success.
          </p>
          <NavLink
            to="/services"
            className="inline-block px-6 sm:px-8 py-2 sm:py-3 bg-white text-jnanakshi-500 font-semibold rounded-lg shadow-lg hover:bg-gray-100 transition text-sm sm:text-base"
            onClick={() => trackCTA('explore_services_about')}
          >
            Explore Our Services
          </NavLink>
        </div>
      </section>

      {/* Footer Component */}
      <footer className="bg-gray-800 dark:bg-dark-800 text-white py-8 sm:py-10 lg:py-12 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* About Section */}
            <div className="sm:col-span-2 lg:col-span-1">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">About Jnanakshi Solutions</h3>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                At Jnanakshi Solutions, we are committed to bridging the critical gap between academic knowledge 
                and professional competence. We specialize in providing immersive live workshops and targeted 
                training programs designed to empower college students and transform them into industry-ready professionals.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <NavLink
                    to="/about-us"
                    className="text-gray-400 dark:text-gray-300 hover:text-white transition text-sm sm:text-base"
                  >
                    About Us
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/services"
                    className="text-gray-400 dark:text-gray-300 hover:text-white transition text-sm sm:text-base"
                  >
                    Services
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Contact Us</h3>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base">
                <strong>Address:</strong> Bangalore, India
              </p>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base mt-2">
                <strong>Phone:</strong> +91 7760556768
              </p>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base mt-2">
                <strong>Email:</strong> support@jnanakshi.com
              </p>
            </div>
          </div>

          <div className="mt-8 sm:mt-10 border-t border-gray-700 dark:border-dark-600 pt-4 sm:pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Social Media Links */}
            <div className="flex space-x-4 sm:space-x-6">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-twitter"></i>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-instagram"></i>
              </a>
            </div>

            {/* Copyright */}
            <p className="text-gray-400 dark:text-gray-300 text-xs sm:text-sm text-center sm:text-right">
              &copy; {new Date().getFullYear()} Jnanakshi Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Chatbot Component */}
      <Chatbot />
    </div>
  );
};

export default AboutUs;








//       {/* Managing Director's Message Section */}
      // {/* <section className="bg-gray-50 dark:bg-dark-800 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
      //   <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
      //     <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-white text-center mb-8 sm:mb-10 lg:mb-12">
      //       Message from Our Managing Director
      //     </h2>
      //     <div className="bg-white dark:bg-dark-700 rounded-2xl shadow-lg overflow-hidden">
      //       <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
      //         {/* Image Section */}
      //         <div className="relative bg-gray-100 dark:bg-dark-600 flex items-center justify-center p-6">
      //           <img 
      //             src="/DSC_1044_edit.jpg" 
      //             alt="Managing Director - Jnanakshi Solutions" 
      //             className="w-full max-w-sm h-auto object-contain rounded-lg shadow-md"
      //             loading="lazy"
      //           />
      //         </div>
              
      //         {/* Message Section */}
      //         <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
      //           <div className="mb-4 sm:mb-6">
      //             <h3 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-2">
      //               Sastry Pulya
      //             </h3>
      //             <p className="text-jnanakshi-600 dark:text-jnanakshi-400 font-semibold text-sm sm:text-base">
      //               Managing Director, Jnanakshi Solutions
      //             </p>
      //           </div>
                
      //           <blockquote className="text-gray-600 dark:text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed italic">
      //             "At Jnanakshi Solutions, we believe that the gap between academic knowledge and professional competence is not just a challenge it's an opportunity. Our mission is to transform this gap into a bridge that connects ambitious students with industry ready skills and real-world expertise.
                  
      //             Through our immersive workshops, exclusive training programs, and personalized mentoring, we don't just teach we inspire, guide, and empower the next generation of professionals. Every student who walks through our doors represents potential waiting to be unlocked.
                  
      //             We are committed to excellence, connection, transformation, and empowerment. These are not just words on our website they are the values that drive everything we do. Together, we're not just building careers; we are building futures."
      //           </blockquote>
      //         </div>
      //       </div>
      //     </div>
      //   </div>
      // </section> */}